const TIME_ZONE = 'Europe/London';
const holidayRequestCache = new Map();

const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: TIME_ZONE,
  weekday: 'short',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

const weekdayFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: TIME_ZONE,
  weekday: 'long',
});

const dayFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: TIME_ZONE,
  day: '2-digit',
});

// TL delivery countdown.
class TLDeliveryCountdown extends HTMLElement {
  #abortController = null;
  #holidayDates = new Set();
  #observer = null;
  #timer = null;
  #initialized = false;

  connectedCallback() {
    if (this.#abortController) return;

    this.#abortController = new AbortController();
    document.addEventListener('shopify:section:load', this.#handleSectionLoad, {
      signal: this.#abortController.signal
    });

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting || entry.intersectionRatio > 0)) return;

          this.#observer?.disconnect();
          this.#observer = null;
          this.#init();
        },
        { rootMargin: '1000px 0px' }
      );
      this.#observer.observe(this);
      return;
    }

    this.#init();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#abortController?.abort();
    this.#abortController = null;
    if (this.#timer) window.clearInterval(this.#timer);
    this.#timer = null;
    this.#initialized = false;
  }

  #handleSectionLoad = (event) => {
    if (!event.target?.contains?.(this)) return;
    this.disconnectedCallback();
    this.connectedCallback();
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      if (this.#timer) {
        window.clearInterval(this.#timer);
        this.#timer = null;
      }
    } else if (this.#initialized && !this.#timer) {
      this.#update();
      this.#timer = window.setInterval(() => this.#update(), 1000);
    }
  };

  #init() {
    if (this.#initialized) return;

    this.#initialized = true;
    const signal = this.#abortController?.signal;

    this.#update();
    this.#timer = window.setInterval(() => this.#update(), 1000);

    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });

    this.#loadBankHolidays().then((holidayDates) => {
      if (signal?.aborted) return;

      this.#holidayDates = holidayDates;
      this.#update();
    });
  }

  async #loadBankHolidays() {
    if (this.dataset.bankHolidaysEnabled !== 'true') return new Set();

    const url = this.dataset.bankHolidaysUrl;
    const region = this.dataset.bankHolidayRegion || 'england-and-wales';
    if (!url) return new Set();

    return getBankHolidayDates(url, region);
  }

  #update() {
    const now = new Date();
    const localNow = getLocalParts(now);
    const dispatchDay = this.#getNextDispatchDay(localNow, now);

    // same-day only before cutoff
    if (dispatchDay !== localNow.dateKey) {
      this.#setAvailableState();
      return;
    }

    const cutoffDate = getZonedDateFromDateKey(dispatchDay, this.#cutoffHour, this.#cutoffMinute);
    const deliveryDay = this.#getNextDeliveryDay(addDays(dispatchDay, 1));

    this.#setActiveState(cutoffDate, deliveryDay, dispatchDay, localNow.dateKey);
  }

  #setActiveState(cutoffDate, deliveryDay, dispatchDay, todayKey) {
    setText(this.#labelElement, this.#getDeliveryLabel(deliveryDay, dispatchDay, todayKey));
    setText(this.#timerPrefixElement, this.#getOrderPrefix());
    setText(this.#timerValueElement, formatDuration(cutoffDate.getTime() - Date.now(), this.#durationLabels));
    setHidden(this.#timerElement, false);
    setHidden(this.#separatorElement, false);
    this.toggleAttribute('data-delivery-countdown-active', true);
  }

  #setAvailableState() {
    setText(this.#labelElement, this.dataset.availableLabel || '');
    setText(this.#timerPrefixElement, '');
    setText(this.#timerValueElement, '');
    setHidden(this.#timerElement, true);
    setHidden(this.#separatorElement, true);
    this.toggleAttribute('data-delivery-countdown-active', false);
  }

  #getDeliveryLabel(deliveryDay, dispatchDay, todayKey) {
    if (dispatchDay === todayKey && deliveryDay === addDays(todayKey, 1)) {
      return this.dataset.tomorrowLabel || this.#getDeliveryDateLabel(deliveryDay);
    }

    return this.#getDeliveryDateLabel(deliveryDay);
  }

  #getDeliveryDateLabel(dateKey) {
    const prefix = (this.dataset.deliveryPrefix || 'Get it').trim();
    const dateLabel = `${getWeekdayName(dateKey)} ${getDayOfMonth(dateKey)}`;

    return `${prefix}${prefix.toLowerCase().endsWith(' by') ? '' : ' by'} ${dateLabel}`.trim();
  }

  #getOrderPrefix() {
    const prefix = (this.dataset.orderPrefix || 'Order within').trim();

    return prefix.toLowerCase() === 'order in' ? 'Order within' : prefix;
  }

  #getNextDispatchDay(localNow, now) {
    const cutoffDate = getZonedDate(localNow, this.#cutoffHour, this.#cutoffMinute);

    if (this.#isDispatchDay(localNow.dateKey) && now.getTime() < cutoffDate.getTime()) {
      return localNow.dateKey;
    }

    let nextDateKey = addDays(localNow.dateKey, 1);

    while (!this.#isDispatchDay(nextDateKey)) {
      nextDateKey = addDays(nextDateKey, 1);
    }

    return nextDateKey;
  }

  #getNextDeliveryDay(dateKey) {
    let nextDateKey = dateKey;

    while (!this.#isDeliveryDay(nextDateKey)) {
      nextDateKey = addDays(nextDateKey, 1);
    }

    return nextDateKey;
  }

  #isDispatchDay(dateKey) {
    const day = getDayOfWeek(dateKey);
    return day >= 1 && day <= 5 && !this.#holidayDates.has(dateKey);
  }

  #isDeliveryDay(dateKey) {
    const day = getDayOfWeek(dateKey);
    const isWeekday = day >= 1 && day <= 5;
    const isSaturday = this.dataset.saturdayDeliveryEnabled === 'true' && day === 6;
    const isSunday = this.dataset.sundayDeliveryEnabled === 'true' && day === 0;

    return (isWeekday || isSaturday || isSunday) && !this.#holidayDates.has(dateKey);
  }

  get #cutoffHour() {
    return clampNumber(Number(this.dataset.cutoffHour), 0, 23, 16);
  }

  get #cutoffMinute() {
    return clampNumber(Number(this.dataset.cutoffMinute), 0, 59, 0);
  }

  get #durationLabels() {
    return {
      day: this.dataset.unitDay || 'Day',
      hour: this.dataset.unitHour || 'Hr',
      minute: this.dataset.unitMinute || 'Min',
      second: this.dataset.unitSecond || 'Sec',
    };
  }

  get #labelElement() {
    return this.querySelector('[data-delivery-countdown-label]');
  }

  get #timerElement() {
    return this.querySelector('[data-delivery-countdown-timer]');
  }

  get #timerPrefixElement() {
    return this.querySelector('[data-delivery-countdown-prefix]');
  }

  get #timerValueElement() {
    return this.querySelector('[data-delivery-countdown-time]');
  }

  get #separatorElement() {
    return this.querySelector('[data-delivery-countdown-separator]');
  }
}

async function getBankHolidayDates(url, region) {
  const cacheKey = `${region}:${url}`;
  if (holidayRequestCache.has(cacheKey)) return holidayRequestCache.get(cacheKey);

  const request = fetch(url, { cache: 'force-cache' })
    .then((response) => {
      if (!response.ok) return new Set();
      return response.json();
    })
    .then((data) => {
      const events = data?.[region]?.events;
      if (!Array.isArray(events)) return new Set();

      return new Set(events.map((event) => event?.date).filter(Boolean));
    })
    .catch(() => new Set());

  holidayRequestCache.set(cacheKey, request);
  return request;
}

function getLocalParts(date) {
  const parts = Object.fromEntries(dateFormatter.formatToParts(date).map((part) => [part.type, part.value]));
  const year = Number(parts.year);
  const month = Number(parts.month);
  const day = Number(parts.day);

  return {
    year,
    month,
    day,
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    second: Number(parts.second),
    dateKey: toDateKey(year, month, day),
  };
}

function getZonedDate(localParts, hour, minute) {
  const utcDate = new Date(Date.UTC(localParts.year, localParts.month - 1, localParts.day, hour, minute));
  const offset = getTimeZoneOffset(utcDate);
  return new Date(utcDate.getTime() - offset);
}

function getZonedDateFromDateKey(dateKey, hour, minute) {
  const [year, month, day] = dateKey.split('-').map(Number);

  return getZonedDate({ year, month, day }, hour, minute);
}

function getTimeZoneOffset(date) {
  const localParts = getLocalParts(date);
  const localTimestamp = Date.UTC(
    localParts.year,
    localParts.month - 1,
    localParts.day,
    localParts.hour,
    localParts.minute,
    localParts.second
  );

  return localTimestamp - date.getTime();
}

function addDays(dateKey, days) {
  const [year, month, day] = dateKey.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + days));
  return toDateKey(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}

function toDateKey(year, month, day) {
  return [year, month, day].map((value) => String(value).padStart(2, '0')).join('-');
}

function getDayOfWeek(dateKey) {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

function getWeekdayName(dateKey) {
  const [year, month, day] = dateKey.split('-').map(Number);
  return weekdayFormatter.format(new Date(Date.UTC(year, month - 1, day, 12)));
}

function getDayOfMonth(dateKey) {
  const [year, month, day] = dateKey.split('-').map(Number);
  return dayFormatter.format(new Date(Date.UTC(year, month - 1, day, 12)));
}

function formatDuration(milliseconds, labels) {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts = [];

  if (days > 0) parts.push(formatDurationPart(days, 'day', labels));
  if (hours > 0) parts.push(formatDurationPart(hours, 'hour', labels));
  if (minutes > 0) parts.push(formatDurationPart(minutes, 'minute', labels));
  if (days === 0) parts.push(formatDurationPart(seconds, 'second', labels));

  return parts.join(' ');
}

function formatDurationPart(value, unit, labels) {
  return `${value} ${labels[unit] || unit}`;
}

function setText(element, text) {
  if (!(element instanceof Element) || element.textContent === text) return;

  element.textContent = text;
}

function setHidden(element, hidden) {
  if (!(element instanceof HTMLElement) || element.hidden === hidden) return;

  element.hidden = hidden;
}

function clampNumber(value, min, max, fallback) {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}

if (!customElements.get('tl-delivery-countdown')) {
  customElements.define('tl-delivery-countdown', TLDeliveryCountdown);
}
